import React, { useContext, useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useParams } from "react-router-dom";
import { AuthContext } from "../../../../config/AuthContext";
import { GetJutgePracticaIdDocumentId } from "../../../../dao/jutge/GetJutgePracticaIdDocumentId";
import { PutJutgePracticaIdDocumentId } from "../../../../dao/jutge/PutJutgePracticaIdDocumentId";
import { PostJutgePracticaIdDocumentId } from "../../../../dao/jutge/PostJutgePracticaIdDocumentId";
import Editor from "@monaco-editor/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { 
    Button, 
    Space, 
    Tag, 
    Empty, 
    Spin, 
    Tooltip, 
    Segmented, 
    message, 
    Card, 
    Row, 
    Col, 
    Statistic, 
    Table, 
    Input, 
    Switch, 
    Modal,
    Drawer,
    Typography,
    Divider,
    Badge
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { 
    FileTextOutlined, 
    SettingOutlined, 
    EditOutlined, 
    PlusOutlined, 
    ReloadOutlined,
    SaveOutlined,
    CodeOutlined,
    CheckCircleOutlined,
    SearchOutlined,
    EyeOutlined,
    CheckOutlined,
    CloseOutlined,
    FileDoneOutlined,
    FolderOpenOutlined,
    ExperimentOutlined
} from "@ant-design/icons";
import EditDocumentModal from "../../problemes/content/EditDocumentModal";

const { Title, Text } = Typography;

interface Props {
    doc?: any;
    documents?: any[];
    loading?: boolean;
    onSelectDoc?: (doc: any) => void;
    practicaNom?: string;
    onRefresh?: () => void;
    propIdPractica?: string;
    // Mode de vista inicial. Per defecte, els admins veuen 'management'; però quan
    // s'incrusta en la vista de la pràctica (modal Revisar) volem obrir 'preview'.
    defaultViewMode?: 'preview' | 'management';
}

const PracticaIdEnunciat: React.FC<Props> = ({ 
    doc, 
    documents = [], 
    loading = false,
    onSelectDoc, 
    practicaNom,
    onRefresh,
    propIdPractica,
    defaultViewMode
}) => {
    const { isLoggedIn, user, userId } = useContext(AuthContext);
    const { id_practica: param_id_practica } = useParams();
    const id_practica = propIdPractica || param_id_practica;
    const isAdmin = user?.tipus === "0" || user?.tipus === "1";

    // Navigation & view states
    const [viewMode, setViewMode] = useState<'preview' | 'management'>(defaultViewMode || (isAdmin ? 'management' : 'preview'));
    const [filterTab, setFilterTab] = useState<'inclosos' | 'disponibles' | 'tots'>('inclosos');
    const [filterTipo, setFilterTipo] = useState<string>('all');
    const [searchText, setSearchText] = useState('');

    // Selected document for preview / code editing
    const [currentDoc, setCurrentDoc] = useState<any>(doc);
    const [loadingDoc, setLoadingDoc] = useState(false);
    const [loadingDocName, setLoadingDocName] = useState<string>("");
    const [codeContent, setCodeContent] = useState<string>("");
    const [hasCodeChanges, setHasCodeChanges] = useState(false);
    const [savingCode, setSavingCode] = useState(false);

    // Cache of already loaded documents (chunked loading)
    const [docCache, setDocCache] = useState<Record<string, any>>({});

    // Edit modal
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingDoc, setEditingDoc] = useState<any>(null);

    // Sync current doc
    useEffect(() => {
        if (doc && (!currentDoc || doc.id !== currentDoc?.id)) {
            loadDocDetails(doc.id, doc.nom);
        } else if (!doc && documents.length > 0 && !currentDoc) {
            const firstActive = documents.find(d => d.visible === 1) || documents[0];
            if (firstActive) loadDocDetails(firstActive.id, firstActive.nom);
        }
    }, [doc, documents]);

    async function loadDocDetails(docId: number | string, docName?: string) {
        if (!docId) return;
        const key = String(docId);
        if (docCache[key]) {
            setCurrentDoc(docCache[key]);
            setCodeContent(decode(docCache[key]?.enunciat));
            setHasCodeChanges(false);
            return;
        }

        setLoadingDoc(true);
        if (docName) setLoadingDocName(docName);
        try {
            const data = await GetJutgePracticaIdDocumentId(id_practica + "", key);
            setCurrentDoc(data);
            setCodeContent(decode(data?.enunciat));
            setHasCodeChanges(false);
            setDocCache(prev => ({ ...prev, [key]: data }));
        } catch (error) {
            console.error("Error carregant document", error);
        } finally {
            setLoadingDoc(false);
            setLoadingDocName("");
        }
    }

    function decode(value: any) {
        if (!value || typeof value !== "string") return "";
        return value.trim();
    }

    // Toggle inclusion (visible 1/0)
    const handleToggleInclude = async (record: any, checked: boolean) => {
        try {
            await PostJutgePracticaIdDocumentId(id_practica + "", record.id);
            message.success(checked ? `"${record.nom}" inclòs a la pràctica` : `"${record.nom}" tret de la pràctica`);
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error(error);
            message.error("Error canviant l'estat del document");
        }
    };

    // Save code directly from Monaco Editor
    const handleSaveCode = async () => {
        if (!currentDoc?.id) return;
        setSavingCode(true);
        try {
            const updatedDoc = {
                ...currentDoc,
                enunciat: codeContent
            };
            await PutJutgePracticaIdDocumentId(currentDoc.id, updatedDoc);
            message.success(`Document "${currentDoc.nom}" desat correctament`);
            setHasCodeChanges(false);
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error(error);
            message.error("Error al desar el document");
        } finally {
            setSavingCode(false);
        }
    };

    const handleCreateNew = (type: string = 'enunciat') => {
        setEditingDoc({
            id: 0,
            nom: '',
            tipo: type,
            visible: 1,
            enunciat: '',
            descripcio: ''
        });
        setEditModalVisible(true);
    };

    const handleEditDoc = (item: any) => {
        setEditingDoc(item);
        setEditModalVisible(true);
    };

    const handleSelectForView = (record: any) => {
        loadDocDetails(record.id);
        if (onSelectDoc) onSelectDoc(record);
        setViewMode('preview');
    };

    // Helper functions for tags
    const getTagColor = (tipo: string) => {
        switch (tipo) {
            case 'enunciat': return 'blue';
            case 'es': return 'purple';
            case 'junit':
            case 'test': return 'green';
            case 'codi': return 'orange';
            default: return 'default';
        }
    };

    const getTipoLabel = (tipo: string) => {
        switch (tipo) {
            case 'enunciat': return 'Enunciat';
            case 'es': return 'Entrada / Sortida';
            case 'junit':
            case 'test': return 'Tests unitaris';
            case 'codi': return 'Codi';
            default: return tipo;
        }
    };

    // KPI stats calculations
    const totalDocs = documents.length;
    const inclososDocs = documents.filter(d => d.visible === 1);
    const noInclososDocs = documents.filter(d => d.visible !== 1);
    const enunciatsCount = documents.filter(d => d.tipo === 'enunciat' && d.visible === 1).length;
    const testsCount = documents.filter(d => (d.tipo === 'junit' || d.tipo === 'test') && d.visible === 1).length;
    const esCount = documents.filter(d => d.tipo === 'es' && d.visible === 1).length;

    // Filter documents for the table
    const filteredDocs = documents.filter(docItem => {
        // Tab filter: inclosos / disponibles / tots
        if (filterTab === 'inclosos' && docItem.visible !== 1) return false;
        if (filterTab === 'disponibles' && docItem.visible === 1) return false;

        // Tipo filter
        if (filterTipo !== 'all' && docItem.tipo !== filterTipo) return false;

        // Search text
        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            const matchesNom = docItem.nom && docItem.nom.toLowerCase().includes(q);
            const matchesDesc = docItem.descripcio && docItem.descripcio.toLowerCase().includes(q);
            const matchesId = String(docItem.id).includes(q);
            if (!matchesNom && !matchesDesc && !matchesId) return false;
        }

        return true;
    });

    const isCodeType = currentDoc?.tipo === 'codi' || currentDoc?.tipo === 'test' || currentDoc?.tipo === 'junit';

    // Columns matching Alumnes aesthetic
    const columns: ColumnsType<any> = [
        {
            title: 'Estat',
            key: 'visible',
            width: 120,
            align: 'center',
            render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <Switch
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                        checked={record.visible === 1}
                        onChange={(checked) => handleToggleInclude(record, checked)}
                        disabled={!isAdmin}
                    />
                    <Tag color={record.visible === 1 ? 'success' : 'default'} style={{ margin: 0, fontSize: 11 }}>
                        {record.visible === 1 ? 'Inclòs' : 'No inclòs'}
                    </Tag>
                </div>
            )
        },
        {
            title: 'Document',
            dataIndex: 'nom',
            key: 'nom',
            render: (text, record) => (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text strong style={{ fontSize: 14 }}>{text}</Text>
                        <Tag color={getTagColor(record.tipo)} style={{ margin: 0, fontSize: 11, fontWeight: 600 }}>
                            {getTipoLabel(record.tipo)}
                        </Tag>
                    </div>
                    {record.descripcio && (
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                            {record.descripcio}
                        </div>
                    )}
                    <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 2 }}>
                        ID #{record.id}
                    </div>
                </div>
            )
        },
        {
            title: 'Tipus',
            dataIndex: 'tipo',
            key: 'tipo_col',
            width: 140,
            render: (tipo) => (
                <Tag color={getTagColor(tipo)} style={{ fontWeight: 500 }}>
                    {getTipoLabel(tipo)}
                </Tag>
            ),
            filters: [
                { text: 'Enunciat', value: 'enunciat' },
                { text: 'Exemple E/S', value: 'es' },
                { text: 'Tests unitaris', value: 'junit' },
                { text: 'Codi', value: 'codi' }
            ],
            onFilter: (value, record) => record.tipo === value
        },
        {
            title: 'Accions',
            key: 'actions',
            width: 220,
            align: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Veure document a pantalla completa / editor">
                        <Button
                            icon={<EyeOutlined />}
                            size="small"
                            type={currentDoc?.id === record.id ? "primary" : "default"}
                            onClick={() => handleSelectForView(record)}
                        >
                            Veure
                        </Button>
                    </Tooltip>
                    {isAdmin && (
                        <Tooltip title="Editar metadades del document">
                            <Button
                                icon={<EditOutlined />}
                                size="small"
                                onClick={() => handleEditDoc(record)}
                            />
                        </Tooltip>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: '8px 0', minHeight: '100%' }}>
            {/* Top Header matching Alumnes aesthetics */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 16,
                paddingBottom: 20,
                marginBottom: 24,
                borderBottom: '1px solid #f0f0f0'
            }}>
                <div>
                    <h2 style={{ margin: 0, fontWeight: 700, color: '#1f1f1f', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <FileTextOutlined style={{ color: '#1890ff' }} />
                        {practicaNom || "Activitat & Documents"}
                    </h2>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        Gestió integral dels documents de la pràctica: Enunciats, Entrada / Sortida, Tests unitaris i Codi
                    </Text>
                </div>

                <Space wrap size="middle">
                    {isAdmin && (
                        <Segmented
                            value={viewMode}
                            onChange={(val) => setViewMode(val as any)}
                            options={[
                                { label: 'Gestió de Documents', value: 'management', icon: <SettingOutlined /> },
                                { label: 'Vista de Treball / Enunciat', value: 'preview', icon: <EyeOutlined /> }
                            ]}
                            size="large"
                        />
                    )}

                    {isAdmin && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            size="large"
                            onClick={() => handleCreateNew('enunciat')}
                            style={{ borderRadius: 8, fontWeight: 600 }}
                        >
                            Nou Document
                        </Button>
                    )}

                    {onRefresh && (
                        <Tooltip title="Actualitzar">
                            <Button
                                icon={<ReloadOutlined />}
                                size="large"
                                onClick={onRefresh}
                                shape="circle"
                            />
                        </Tooltip>
                    )}
                </Space>
            </div>

            {/* KPI Statistics Row (Same as Alumnes aesthetic) */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={8}>
                    <Card bordered={false} style={{ boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)', borderRadius: 8 }}>
                        <Statistic 
                            title="Enunciats Actius" 
                            value={enunciatsCount} 
                            valueStyle={{ color: '#1890ff', fontWeight: 700 }}
                            prefix={<FileTextOutlined />} 
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                            Documents d'enunciat actius
                        </div>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card bordered={false} style={{ boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)', borderRadius: 8 }}>
                        <Statistic 
                            title="Entrada / Sortida" 
                            value={esCount} 
                            valueStyle={{ color: '#52c41a', fontWeight: 700 }}
                            prefix={<FileDoneOutlined />} 
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                            Documents de tipus entrada / sortida actius
                        </div>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card bordered={false} style={{ boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)', borderRadius: 8 }}>
                        <Statistic 
                            title="Tests Unitaris (JUnit)" 
                            value={testsCount} 
                            valueStyle={{ color: '#722ed1', fontWeight: 700 }}
                            prefix={<ExperimentOutlined />} 
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                            Avaluació automàtica del codi
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* View Mode: MANAGEMENT (Documents Table with Inclosos / Disponibles) */}
            {viewMode === 'management' && (
                <Card 
                    bordered={false} 
                    style={{ borderRadius: 8, boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04)' }}
                    bodyStyle={{ padding: 20 }}
                >
                    {/* Filter and Search Bar */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 12,
                        marginBottom: 16
                    }}>
                        <Space size="middle" wrap>
                            <Segmented
                                value={filterTab}
                                onChange={(val) => setFilterTab(val as any)}
                                options={[
                                    { label: `Inclosos (${inclososDocs.length})`, value: 'inclosos' },
                                    { label: `No Inclosos / Disponibles (${noInclososDocs.length})`, value: 'disponibles' },
                                    { label: `Tots (${totalDocs})`, value: 'tots' }
                                ]}
                            />

                            <Segmented
                                value={filterTipo}
                                onChange={(val) => setFilterTipo(val as any)}
                                options={[
                                    { label: 'Tots els tipus', value: 'all' },
                                    { label: 'Enunciats', value: 'enunciat' },
                                    { label: 'Entrada / Sortida', value: 'es' },
                                    { label: 'Tests', value: 'junit' },
                                    { label: 'Codi', value: 'codi' }
                                ]}
                            />
                        </Space>

                        <Input
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="Cercar document..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            allowClear
                            style={{ width: 240, borderRadius: 6 }}
                        />
                    </div>

                    {loading && (
                        <div style={{ marginBottom: 16 }}>
                            <Spin tip="Carregant documents i recursos de la pràctica..." size="default">
                                <div style={{ height: 40, background: '#fafafa', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: 13 }}>Sincronitzant documents...</Text>
                                </div>
                            </Spin>
                        </div>
                    )}

                    <Table
                        columns={columns}
                        dataSource={filteredDocs}
                        rowKey="id"
                        pagination={{ pageSize: 8 }}
                        size="middle"
                        loading={loading}
                        style={{ marginTop: 8 }}
                    />
                </Card>
            )}

            {/* View Mode: PREVIEW & CONSOLE EDITING */}
            {viewMode === 'preview' && (
                <div>
                    {/* Active Document Selector Bar */}
                    <div style={{
                        background: '#ffffff',
                        padding: '12px 16px',
                        borderRadius: 8,
                        marginBottom: 16,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 12
                    }}>
                        <Space wrap size="small">
                            <span style={{ fontWeight: 600, color: '#595959', marginRight: 4 }}>
                                Seleccionar Document:
                            </span>
                            {inclososDocs.map((d) => {
                                const isSelected = currentDoc?.id === d.id;
                                return (
                                    <Button
                                        key={d.id}
                                        type={isSelected ? "primary" : "default"}
                                        size="middle"
                                        icon={
                                            d.tipo === 'enunciat' ? <FileTextOutlined /> :
                                            d.tipo === 'es' ? <FileTextOutlined /> : <CodeOutlined />
                                        }
                                        onClick={() => loadDocDetails(d.id)}
                                        style={{ borderRadius: 6 }}
                                    >
                                        <span>{d.nom}</span>
                                        <Tag 
                                            color={getTagColor(d.tipo)} 
                                            style={{ marginLeft: 6, marginRight: 0, fontSize: 10, padding: '0 4px' }}
                                        >
                                            {getTipoLabel(d.tipo)}
                                        </Tag>
                                    </Button>
                                );
                            })}
                        </Space>

                        {isAdmin && isCodeType && (
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleSaveCode}
                                loading={savingCode}
                                disabled={!hasCodeChanges}
                                style={{ background: '#52c41a', borderColor: '#52c41a', borderRadius: 6 }}
                            >
                                Desar canvis de codi
                            </Button>
                        )}
                    </div>

                    {/* Document Content */}
                    {loadingDoc ? (
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 350, gap: 12 }}>
                            <Spin size="large" />
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                Carregant contingut {loadingDocName ? `de "${loadingDocName}"` : "del document"}...
                            </Text>
                        </div>
                    ) : !currentDoc ? (
                        <Empty description="Cap document seleccionat">
                            {isAdmin && (
                                <Button type="primary" onClick={() => setViewMode('management')}>
                                    Gestionar Documents
                                </Button>
                            )}
                        </Empty>
                    ) : (
                        <div>
                            {currentDoc.tipo === 'enunciat' && (
                                <Card 
                                    bordered={false} 
                                    style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
                                    bodyStyle={{ padding: 28 }}
                                >
                                    <div style={{ marginBottom: 16 }}>
                                        <Title level={3} style={{ margin: 0 }}>{currentDoc.nom}</Title>
                                        {currentDoc.descripcio && (
                                            <Text type="secondary">{currentDoc.descripcio}</Text>
                                        )}
                                    </div>
                                    <Divider style={{ margin: '12px 0 24px 0' }} />
                                    <div
                                        className="enunciat-container"
                                        style={{ lineHeight: 1.7 }}
                                        dangerouslySetInnerHTML={{ __html: decode(currentDoc.enunciat) }}
                                    />
                                </Card>
                            )}

                            {currentDoc.tipo === 'es' && (
                                <Card 
                                    bordered={false} 
                                    style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
                                    bodyStyle={{ padding: 28 }}
                                >
                                    <div style={{ marginBottom: 16 }}>
                                        <Title level={3} style={{ margin: 0 }}>{currentDoc.nom}</Title>
                                        {currentDoc.descripcio && (
                                            <Text type="secondary">{currentDoc.descripcio}</Text>
                                        )}
                                    </div>
                                    <Divider style={{ margin: '12px 0 24px 0' }} />
                                    <div className="es-container">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                                            {decode(currentDoc.enunciat)}
                                        </ReactMarkdown>
                                    </div>
                                </Card>
                            )}

                            {isCodeType && (
                                <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #d9d9d9', background: '#fff' }}>
                                    <div style={{
                                        padding: '10px 16px',
                                        background: '#1e1e1e',
                                        borderBottom: '1px solid #333',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Space size="middle">
                                            <CodeOutlined style={{ color: '#52c41a' }} />
                                            <span style={{ fontWeight: 600, color: '#f0f0f0', fontSize: 13 }}>
                                                {currentDoc.nom} ({getTipoLabel(currentDoc.tipo)})
                                            </span>
                                            {hasCodeChanges && (
                                                <Tag color="warning">Canvis pendents de desar</Tag>
                                            )}
                                        </Space>
                                        {isAdmin && (
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<SaveOutlined />}
                                                onClick={handleSaveCode}
                                                loading={savingCode}
                                                disabled={!hasCodeChanges}
                                                style={{ borderRadius: 4 }}
                                            >
                                                Desar Canvis
                                            </Button>
                                        )}
                                    </div>
                                    <Editor
                                        height="650px"
                                        defaultLanguage="java"
                                        value={codeContent}
                                        onChange={(val) => {
                                            if (isAdmin) {
                                                setCodeContent(val || "");
                                                setHasCodeChanges(true);
                                            }
                                        }}
                                        theme="vs-dark"
                                        options={{
                                            readOnly: !isAdmin,
                                            automaticLayout: true,
                                            minimap: { enabled: false },
                                            fontSize: 14,
                                            lineNumbers: "on",
                                            scrollBeyondLastLine: false
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Edit Document Modal */}
            {editModalVisible && (
                <EditDocumentModal
                    visible={editModalVisible}
                    document={editingDoc}
                    onClose={(updated) => {
                        setEditModalVisible(false);
                        setEditingDoc(null);
                        if (updated && onRefresh) onRefresh();
                    }}
                    idPractica={id_practica}
                    practicaNom={practicaNom}
                />
            )}
        </div>
    );
};

export default PracticaIdEnunciat;


